import { FlatTreeControl } from "@angular/cdk/tree";
import {
  ChangeDetectionStrategy,
  Component,
  Inject,
  signal,
} from "@angular/core";
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from "@angular/material/dialog";
import {
  MatTreeFlatDataSource,
  MatTreeFlattener,
} from "@angular/material/tree";
import { BehaviorSubject, debounceTime, map } from "rxjs";
import { getColorCode } from "src/app/core/util/color";
import { modalConfig, ModalEnum } from "src/app/core/util/modalConfig";
import { Globals } from "src/app/globals";
import { SharedProjectChatPanelComponent } from "../shared-project-chat-panel/shared-project-chat-panel.component";
import { SharedProjectService } from "src/app/core/service/api/shared-project.service";
import { GeneralService } from "src/app/core/service/api/general.service";
import { ProjectConfigsService } from "src/app/akita/project-configs/state/project-configs.service";
import { GenericComponent } from "src/app/core/util/abstract/generic-component";

@Component({
  selector: "app-chat-channel-sidebar",
  templateUrl: "./chat-channel-sidebar.component.html",
  styleUrl: "./chat-channel-sidebar.component.css",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChatChannelSidebarComponent extends GenericComponent {
  // Original data backup
  originalData: any[];
  notificationCounts: any;
  searchTerm = "";
  AvatarCustomStyleSelector = {
    "border-width": "3px",
    "font-weight": "bold",
    width: "27px",
  };
  AvatarCustomStyle = {
    "border-width": "3px",
    "font-weight": "bold",
    width: "37px",
  };
  SmallAvatarCustomStyle = {
    "border-width": "3px",
    "font-weight": "bold",
    width: "25px",
  };

  private _transformer = (node: any, level: number) => {
    return {
      ...node,
      expandable: !!node.children && node.children.length > 0,
      level: level,
    };
  };
  treeControl = new FlatTreeControl<any>(
    (node) => node.level,
    (node) => node.expandable,
  );
  treeFlattener = new MatTreeFlattener(
    this._transformer,
    (node) => node.level,
    (node) => node.expandable,
    (node) => node.children,
  );
  dataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);
  hasChild = (_: number, node: any) => node.expandable;
  private searchSubject = new BehaviorSubject<string>("");
  isLoading = signal(true);

  constructor(
    public globals: Globals,
    @Inject(MAT_DIALOG_DATA) public data,
    private dialog: MatDialog,
    private dialogRef: MatDialogRef<ChatChannelSidebarComponent>,
    private projectConfigs: ProjectConfigsService,
    private _sharedProjectsService: SharedProjectService,
    private _generalService: GeneralService,
  ) {
    super(globals);
  }

  ngOnInit(): void {
    this.getChatNotifications();
    this.searchSubject
      .pipe(
        debounceTime(300),
        map((searchTerm) => {
          if (!searchTerm) {
            // show full tree if empty search
            return this.originalData;
          }
          // filter tree when searching
          return this.filterTree(this.originalData, searchTerm.toLowerCase());
        }),
      )
      .subscribe((filteredData) => {
        if (filteredData) this.dataSource.data = filteredData;

        if (this.searchTerm) {
          setTimeout(() => {
            this.expandMatchingNodes(this.searchTerm.toLowerCase());
          });
        }
        if (!this.searchTerm) {
          setTimeout(() => {
            const firstNode = this.treeControl?.dataNodes?.[0];
            if (firstNode) {
              this.treeControl.expand(firstNode);
            }
          });
        }
      });
  }

  getChatNotifications() {
    this.projectConfigs.getChatNotifications().subscribe((res: any) => {
      const notifications = res.filter(
        (item: any) => item.data.data.type === "comment_posted",
      );
      this.notificationCounts =
        this.countNotificationsByProjectCode(notifications);
      this.initEntities();
    });
  }

  countNotificationsByProjectCode(notifications: any[]) {
    return notifications?.reduce((acc: any, item: any) => {
      const sharedProjectId = item?.data?.data?.metadata?.id;
      const notificationId = item?.id;

      if (!sharedProjectId || !notificationId) return acc;

      if (!acc[sharedProjectId]) {
        acc[sharedProjectId] = {
          count: 0,
          notificationIds: [],
        };
      }

      acc[sharedProjectId].count += 1;
      acc[sharedProjectId].notificationIds.push(notificationId);

      return acc;
    }, {});
  }

  initEntities() {
    this.isLoading.set(true);
    this._sharedProjectsService.getSharedProjects().subscribe((res) => {
      const byTypeAndModule = this.filterSharedProjectsByTypeAndModule(
        res ?? [],
      );
      const filtered = byTypeAndModule
        .filter(
          (item) =>
            (item.shared_level === "chat" || item.shared_level === "read") &&
            item.status === "accepted",
        )
        .map((item) => {
          let name = "";
          if (this.globals.project.projects_id === item.source_project.id) {
            name = item.target_project.name;
          } else if (
            this.globals.project.projects_id === item.target_project.id
          ) {
            name = item.source_project.name;
          }
          const color = getColorCode();
          const notificationMeta = this.notificationCounts?.[item.id];
          const notificationCount = notificationMeta?.count || 0;
          const notificationIds = notificationMeta?.notificationIds || [];
          return {
            ...item,
            name,
            color,
            is_shared_project: true,
            notificationCount,
            notificationIds,
          };
        });
      // subprojects + shared projects
      const filteredSubprojects = this.globals.subprojects.filter(
        (sp) => sp?.tenant_type !== "shared",
      );
      const allSubprojects = [...filtered, ...filteredSubprojects];

      // Clone to avoid mutating the shared globals.project, and override `id`
      // with `projects_id` because the projects list endpoint joins
      // users_projects which causes PDO to overwrite projects.id with
      // users_projects.id. The chat panel sends `data.id` as `type_id` and
      // the backend does Projects::findOrFail($type_id), so we need the real
      // main DB project id here.
      const project: any = {
        ...this.globals.project,
        parent_id: null,
        color: getColorCode(),
        is_main_project: true,
        id: this.globals.project.projects_id ?? this.globals.project.id,
        children: [...this.reorderSubprojects(allSubprojects)],
      };
      this.originalData = [project];
      this.dataSource.data = [project];
      this.searchSubject.next("");

      if (this.data?.openChatPanel) this.openChatInternal(project.children);
      this.isLoading.set(false);
    });
  }

  private openChatInternal(children: any[]) {
    if (this.data?.openChatPanel) {
      const metadata = this.data.item.data.data.metadata;
      const node = children.find((item) => {
        if (
          item?.source_project?.code === metadata.source_project_code &&
          item?.target_project?.code === metadata.target_project_code
        ) {
          return item;
        }
      });
      if (node) this.openChatPanel(node);
    }
  }

  reorderSubprojects(allSubprojects: any[]): any[] {
    const reordered = [...allSubprojects].sort((a, b) => {
      const aIsSharedAccepted =
        (a.shared_level === "chat" || a.shared_level === "read") &&
        a.status === "accepted";
      const bIsSharedAccepted =
        (b.shared_level === "chat" || b.shared_level === "read") &&
        b.status === "accepted";

      if (aIsSharedAccepted && !bIsSharedAccepted) return 1; // shared goes later
      if (!aIsSharedAccepted && bIsSharedAccepted) return -1; // non-shared first
      return 0;
    });

    const firstSharedIndex = reordered.findIndex(
      (item) =>
        (item.shared_level === "chat" || item.shared_level === "read") &&
        item.status === "accepted",
    );

    if (firstSharedIndex !== -1) {
      reordered[firstSharedIndex] = {
        ...reordered[firstSharedIndex],
        is_first_shared: true,
      };
    }

    return reordered;
  }

  onSearchInput(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.searchTerm = value;
    this.searchSubject.next(value);
  }

  isChildOfLastSibling(node: any): boolean {
    if (this.treeControl.getLevel(node) <= 1) return false;
    const parent = this.getParentNode(node);
    if (!parent) return false;
    return this.isLastSibling(parent);
  }

  isLastSibling(node: any): boolean {
    const parent = this.getParentNode(node);
    if (!parent) {
      const siblings = this.treeControl.dataNodes.filter(
        (n) => this.treeControl.getLevel(n) === 0,
      );
      return siblings.indexOf(node) === siblings.length - 1;
    }
    const siblings = this.treeControl.dataNodes.filter(
      (n) =>
        this.getParentNode(n)?.id === parent.id &&
        this.treeControl.getLevel(n) === this.treeControl.getLevel(node),
    );

    return siblings.indexOf(node) === siblings.length - 1;
  }

  getParentNode(node: any): any {
    const currentLevel = this.treeControl.getLevel(node);
    if (currentLevel < 1) return null;
    const nodeIndex = this.treeControl.dataNodes.indexOf(node);
    for (let i = nodeIndex - 1; i >= 0; i--) {
      const currentNode = this.treeControl.dataNodes[i];
      if (this.treeControl.getLevel(currentNode) < currentLevel) {
        return currentNode;
      }
    }
    return null;
  }

  isGrandparentLastSibling(node: any): boolean {
    const currentLevel = this.treeControl.getLevel(node);
    if (currentLevel < 2) return false;
    const parent = this.getParentNode(node);
    if (!parent) return false;
    const grandparent = this.getParentNode(parent);
    if (!grandparent) return false;
    return this.isLastSibling(grandparent);
  }

  isGreatGrandparentLastSibling(node: any): boolean {
    if (this.treeControl.getLevel(node) < 3) return false;
    const parent = this.getParentNode(node);
    if (!parent) return false;
    const grandparent = this.getParentNode(parent);
    if (!grandparent) return false;
    const greatGrandparent = this.getParentNode(grandparent);
    if (!greatGrandparent) return false;
    return this.isLastSibling(greatGrandparent);
  }

  private filterTree(nodes: any[], searchTerm: string): any[] {
    const result: any[] = [];

    for (const node of nodes) {
      // recursively filter children
      const filteredChildren = node.children
        ? this.filterTree(node.children, searchTerm)
        : [];

      // check if current node matches
      const isMatch = node.name.toLowerCase().includes(searchTerm);

      if (isMatch || filteredChildren.length > 0) {
        result.push({
          ...node,
          children: filteredChildren,
        });
      }
    }

    return result;
  }

  private expandMatchingNodes(searchTerm: string): void {
    this.treeControl.collapseAll();

    const matchingNodes = this.treeControl.dataNodes.filter((node) =>
      node.name.toLowerCase().includes(searchTerm),
    );

    matchingNodes.forEach((node) => {
      this.expandParentNodes(node);
    });
  }

  private expandParentNodes(node: any): void {
    const parent = this.getParentNode(node);
    if (parent) {
      this.treeControl.expand(parent);
      this.expandParentNodes(parent);
    }
  }

  private hasMatchingChildren(node: any, searchTerm: string): boolean {
    if (!node.children) return false;
    if (
      node.children.some((child: any) =>
        child.name.toLowerCase().includes(searchTerm),
      )
    ) {
      return true;
    }
    return node.children.some((child: any) =>
      this.hasMatchingChildren(child, searchTerm),
    );
  }

  openChatPanel(node: any): void {
    // if (node.id === this.globals.project.id) return;
    this.dialogRef.close();
    if (
      node.notificationIds &&
      Array.isArray(node.notificationIds) &&
      node.notificationIds.length > 0
    ) {
      this.deleteChatNotifications(node.notificationIds);
    }
    this.dialog.open(
      SharedProjectChatPanelComponent,
      modalConfig(
        {
          data: node,
          panelClass: [
            "animate__animated",
            "animate__slideInRight",
            "chat-sidebar",
          ],
          disableClose: false,
        },
        ModalEnum.SidebarChatMediumResponsive,
      ),
    );
  }

  deleteChatNotifications(notification_ids: any) {
    if (
      !notification_ids ||
      !Array.isArray(notification_ids) ||
      notification_ids.length === 0
    ) {
      return;
    }
    const ids = notification_ids.join(",");
    if (!ids) return;
    this._generalService.clearChatNotifications(ids).subscribe((res) => {});
  }

  ngOnDestroy(): void {
    this.searchSubject.complete();
  }
}
